import { FormRadioButtonGroup } from '@portal_components/components/FormRadioButtonGroup';
import { FormDropdown } from '@portal_components/components/FormDropdown';
import { useStandingData } from '../../hooks/useStandingData';
import { FormTextInput } from '@portal_components/components/FormTextInput';
import { FormDatePicker } from '@portal_components/components/FormDatePicker';
import { EMpanAssignmentMode, EMpanEditMode, MeterPointDetails } from '../../types';
import { FormCard } from '@portal_components/components/FormCard';

interface SiteDetailsTabProps {
  readonly: boolean;
  mode: EMpanAssignmentMode;
  editMode?: EMpanEditMode;
}

export const SiteDetailsTab = ({ readonly, mode, editMode }: SiteDetailsTabProps) => {
  const {
    isLoading,
    standingData: {
      standingDataSiteTypeList,
      standingDataAssetOwnershipList,
      standingDataNoCommsList,
      standingDataSiteStatusList,
      standingDataMeterWorkRequiredList
    }
  } = useStandingData();

  if (isLoading) {
    return <div className="flex flex-row justify-center">Loading standing data...</div>;
  }

  return (
    <FormCard columns={2}>
      <FormDatePicker<MeterPointDetails>
        title="Effective Date"
        name="effectiveDate"
        mandatory
        readonly={readonly || editMode === EMpanEditMode.TRANSFER}
      />
      <FormDropdown<MeterPointDetails>
        title="Site Status"
        name="contractSiteStatusId"
        placeholder="Select Site Status"
        elementsForDisplay={standingDataSiteStatusList}
        readonly={
          readonly ||
          mode === EMpanAssignmentMode.NEW ||
          (mode === EMpanAssignmentMode.EDIT && editMode !== EMpanEditMode.EDIT)
        }
        mandatory
      />
      <FormTextInput<MeterPointDetails>
        title="Site Reference Name"
        name="siteReferenceName"
        readonly={readonly}
      />
      <FormRadioButtonGroup<MeterPointDetails>
        title="Import"
        name="isImport"
        buttons={[
          { title: 'Yes', value: 'Y' },
          { title: 'No', value: 'N' }
        ]}
        readonly={readonly}
        mandatory
      />
      <FormRadioButtonGroup<MeterPointDetails>
        title="Export"
        name="isExport"
        buttons={[
          { title: 'Yes', value: 'Y' },
          { title: 'No', value: 'N' }
        ]}
        readonly={readonly}
        mandatory
      />
      <FormDropdown<MeterPointDetails>
        title="Site Type"
        name="siteTypeId"
        placeholder="Select Site Type"
        elementsForDisplay={standingDataSiteTypeList}
        readonly={readonly}
      />
      <FormDropdown<MeterPointDetails>
        title="Asset Ownership"
        name="assetOwnership"
        placeholder="Select Asset Ownership"
        elementsForDisplay={standingDataAssetOwnershipList}
        readonly={readonly}
      />
      <FormTextInput<MeterPointDetails>
        title="Lease Purchase Term"
        name="leasePurchaseTerm"
        readonly={readonly}
      />
      <FormTextInput<MeterPointDetails> title="Related MPRN" name="relatedMprn" readonly={readonly} />
      <FormTextInput<MeterPointDetails> title="Batch Name" name="batchName" readonly={readonly} />
      <FormDropdown<MeterPointDetails>
        title="Meter Work Required"
        name="meterWorkRequired"
        placeholder="Select Required Meter Work"
        elementsForDisplay={standingDataMeterWorkRequiredList}
        readonly={readonly}
      />
      <FormRadioButtonGroup<MeterPointDetails>
        title="Multi Person Job"
        name="multiPersonJob"
        buttons={[
          { title: 'Yes', value: 'Y' },
          { title: 'No', value: 'N' }
        ]}
        readonly={readonly}
      />
      <FormRadioButtonGroup<MeterPointDetails>
        title="Parking Available"
        name="parkingAvailable"
        buttons={[
          { title: 'Yes', value: 'Y' },
          { title: 'No', value: 'N' }
        ]}
        readonly={readonly}
      />
      <FormTextInput<MeterPointDetails> title="Parking Notes" name="parkingNotes" readonly={readonly} />
      <FormTextInput<MeterPointDetails>
        title="Meter Location Notes"
        name="meterLocationNotes"
        readonly={readonly}
      />
      <FormRadioButtonGroup<MeterPointDetails>
        title="Mobile Signal"
        name="mobileSignal"
        buttons={[
          { title: 'Yes', value: 'Y' },
          { title: 'No', value: 'N' },
          { title: 'Unknown', value: 'U' }
        ]}
        readonly={readonly}
      />
      <FormDropdown<MeterPointDetails>
        title="No Comms"
        name="noCommsId"
        placeholder="Select No Comms"
        elementsForDisplay={standingDataNoCommsList}
        readonly={readonly}
      />
      <FormRadioButtonGroup<MeterPointDetails>
        title="Customer Owned Comms"
        name="custOwnedComms"
        buttons={[
          { title: 'Yes', value: 'Y' },
          { title: 'No', value: 'N' },
          { title: 'N/A', value: undefined }
        ]}
        readonly={readonly}
      />
      <FormTextInput<MeterPointDetails> title="Utility ID" name="utilityId" readonly />
      <FormTextInput<MeterPointDetails> title="Fuse Size" name="fuseSize" readonly={readonly} />
    </FormCard>
  );
};
